// Reusable, pure helpers for parsing and presenting the face-photo validation
// response. Keeping these framework-agnostic makes them easy to unit test and
// reuse across the different upload entry points (dropzone + webcam).
import { TFunction } from 'i18next';

import {
  BACKGROUND_COLOR_RULE,
  COLOR_NAME_MATCH_THRESHOLD,
  INamedColor,
  IPhotoValidationResult,
  IPhotoValidationRule,
  NAMED_COLORS,
  PHOTO_RULE_DISPLAY_NAMES,
  PHOTO_RULE_HOW_TO_FIX,
  PHOTO_RULE_STATUS,
  PHOTO_VALIDATION_MODE,
} from '../constants/photoValidation';

// Fallback used when a brand new backend rule has no explicit mapping yet, e.g.
// "FACE_TILT" -> "Face tilt".
const humanizeRuleName = (ruleName: string): string =>
  (ruleName || '')
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((word, index) =>
      index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word,
    )
    .join(' ');

/**
 * Normalises a raw upload error payload (either the XHR response string used by
 * react-dropzone-uploader, or the parsed object returned by axios) into an
 * IPhotoValidationResult. Returns null when the payload is not a photo
 * validation failure, so callers can fall back to their default error handling.
 */
export const parsePhotoValidationResult = (
  raw: unknown,
): IPhotoValidationResult | null => {
  let data: any = raw;

  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      return null;
    }
  }

  const validationResult = data?.validationResult;
  if (!validationResult || !Array.isArray(validationResult.allRules)) {
    return null;
  }

  const allRules: IPhotoValidationRule[] = validationResult.allRules;
  const failedRules: IPhotoValidationRule[] = Array.isArray(
    validationResult.failedRules,
  )
    ? validationResult.failedRules
    : allRules.filter((rule) => rule.status === PHOTO_RULE_STATUS.FAILED);

  return {
    passed: !!validationResult.passed,
    validationMode: validationResult.validationMode,
    summary: validationResult.summary,
    allRules,
    failedRules,
  };
};

export const getPassedRules = (
  result: IPhotoValidationResult,
): IPhotoValidationRule[] =>
  result.allRules.filter((rule) => rule.status === PHOTO_RULE_STATUS.PASSED);

export const getFailedRules = (
  result: IPhotoValidationResult,
): IPhotoValidationRule[] =>
  result.failedRules && result.failedRules.length
    ? result.failedRules
    : result.allRules.filter((rule) => rule.status === PHOTO_RULE_STATUS.FAILED);

/**
 * True when the backend accepted the upload but reported non-blocking warnings
 * (validationMode === WARN_ONLY with at least one failed rule).
 */
export const isWarnOnlyWithIssues = (
  result: IPhotoValidationResult | null,
): boolean =>
  !!result &&
  result.validationMode === PHOTO_VALIDATION_MODE.WARN_ONLY &&
  getFailedRules(result).length > 0;

/**
 * Failure-focused summary shown in the header badge, e.g. 1 / 14
 * (failed rules / total rules).
 */
export const getPhotoValidationProgress = (
  result: IPhotoValidationResult,
): { failed: number; total: number } => {
  const failed = getFailedRules(result).length;

  return { failed, total: result.allRules.length };
};

// Small wrapper so callers always get a `string`. In this i18next version the
// bare `t` return type is a union (string | object | ...), which is awkward to
// use directly in JSX or string returns.
export const translateWithDefault = (
  t: TFunction,
  key: string,
  defaultValue: string,
): string => t(key, { defaultValue }) as string;

export const getRuleDisplayName = (ruleName: string, t: TFunction): string => {
  const entry = PHOTO_RULE_DISPLAY_NAMES[ruleName];
  return entry
    ? translateWithDefault(t, entry.key, entry.default)
    : humanizeRuleName(ruleName);
};

export const getRuleHowToFix = (ruleName: string, t: TFunction): string => {
  const entry = PHOTO_RULE_HOW_TO_FIX[ruleName];
  return entry ? translateWithDefault(t, entry.key, entry.default) : '';
};

export const isBackgroundColorRule = (ruleName: string): boolean =>
  ruleName === BACKGROUND_COLOR_RULE;

// Validates and normalises a hex string to the `#RRGGBB` form (expanding the
// shorthand `#RGB`). Returns null for anything that is not a hex colour.
export const normalizeHexColor = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }
  const match = value.trim().match(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
  if (!match) {
    return null;
  }
  let hex = match[1];
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('');
  }
  return `#${hex.toUpperCase()}`;
};

// Extracts the first hex colour found within a free-text message (e.g. the
// backend background rule message), normalised to `#RRGGBB`.
export const extractHexColor = (text?: string | null): string | null => {
  if (!text) {
    return null;
  }
  const match = text.match(/#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{3})\b/);
  return match ? normalizeHexColor(match[0]) : null;
};

const hexToRgb = (
  hex: string,
): { r: number; g: number; b: number } | null => {
  const normalized = normalizeHexColor(hex);
  if (!normalized) {
    return null;
  }
  return {
    r: parseInt(normalized.slice(1, 3), 16),
    g: parseInt(normalized.slice(3, 5), 16),
    b: parseInt(normalized.slice(5, 7), 16),
  };
};

/**
 * Returns a friendly, translated colour name for the given hex value by finding
 * the nearest colour in NAMED_COLORS (simple RGB distance). Returns null when no
 * palette colour is close enough, so the caller can show only the swatch.
 */
export const getFriendlyColorName = (
  hex: string,
  t: TFunction,
): string | null => {
  const rgb = hexToRgb(hex);
  if (!rgb) {
    return null;
  }

  let closest: INamedColor | null = null;
  let closestDistance = Number.POSITIVE_INFINITY;

  NAMED_COLORS.forEach((named) => {
    const namedRgb = hexToRgb(named.hex);
    if (!namedRgb) {
      return;
    }
    const distance = Math.sqrt(
      (rgb.r - namedRgb.r) ** 2 +
        (rgb.g - namedRgb.g) ** 2 +
        (rgb.b - namedRgb.b) ** 2,
    );
    if (distance < closestDistance) {
      closestDistance = distance;
      closest = named;
    }
  });

  if (closest && closestDistance <= COLOR_NAME_MATCH_THRESHOLD) {
    return translateWithDefault(t, closest.key, closest.default);
  }
  return null;
};
